import { useMatchRoute, useRouterState } from "@tanstack/react-router"
import type {
  RegisteredRouter,
  UseMatchRouteOptions,
} from "@tanstack/react-router"

/**
 * Whether the given route is currently matched — the React Compiler-safe
 * counterpart to `useMatchRoute`, and what sidebar/nav active states use.
 *
 * `useMatchRoute` hands back a callback that is memoised on the router, so its
 * identity never changes, and which reads the current location from the router
 * internally. The compiler cannot see that hidden dependency: it caches the
 * result of `matchRoute(…)` against the callback alone, so every call site
 * freezes at whatever the answer was when the component first mounted. That is
 * what left the workspace switcher showing the workspace you had just left
 * until a full page load.
 *
 * Matching inside a `useRouterState` selector makes the answer a hook return
 * value instead — the compiler never caches those — and subscribes to the whole
 * router state, so it tracks navigation. Prefer this over `useMatchRoute`.
 */
export default function useIsRouteActive<
  const TFrom extends string = string,
  const TTo extends string | undefined = undefined,
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = "",
>(
  opts: UseMatchRouteOptions<RegisteredRouter, TFrom, TTo, TMaskFrom, TMaskTo>,
): boolean {
  const matchRoute = useMatchRoute()

  return useRouterState({ select: () => !!matchRoute(opts) })
}
